import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CloseButton, Form, Modal as ModalComponent } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import { createOutlet } from '../../apis/queries';
import SubmitButton from '../../components/Buttons/SubmitButton';
import { isValidPhoneNumber } from '../../helpers/validation';
import DisplayFieldErrors from '../../utils/DisplayFormError';
import { notification, NotificationKinds } from '../../widgets/Notifications/notifications';
import { OUTLETS_QUERY_KEY } from './Outlets';

interface CreateOutletModalProps {
  show: boolean;
  handleClose: () => void;
}

interface CreateOutletFormInput {
  name: string;
  phoneNumber: string;
  address: string;
  isActive: boolean;
}

const CreateOutletModal = ({ show, handleClose }: CreateOutletModalProps) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (outlet: CreateOutletFormInput) => {
      return createOutlet(outlet);
    },
    onSuccess: data => {
      const { data: newOutlet } = data;
      notification({
        variant: NotificationKinds.SUCCESS,
        title: `OUTLET CREATED`,
        subtitle: `${newOutlet?.name} has been added`
      });

      handleClose();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: OUTLETS_QUERY_KEY });
    }
  });

  const loading = mutation?.isPending;

  const methods = useForm<CreateOutletFormInput>({
    mode: 'onTouched',
    defaultValues: {
      name: '',
      phoneNumber: '',
      address: '',
      isActive: true
    }
  });

  const {
    getValues,
    register,
    formState: { isValid, errors }
  } = methods;

  const onCreateOutlet = () => {
    const fields = getValues();
    mutation?.mutate(fields);
  };

  return (
    <ModalComponent show={show} onHide={handleClose} centered>
      <ModalComponent.Header>
        <h4 className="card-header-title">New Outlet</h4>
        <CloseButton onClick={handleClose} />
      </ModalComponent.Header>
      <ModalComponent.Body className="overflow-auto d-flex flex-column flex-grow-1">
        <Form>
          <div className="form-group">
            <Form.Label htmlFor="name">Name</Form.Label>
            <Form.Control
              type="text"
              id="name"
              placeholder="Super Store"
              {...register('name', {
                required: 'Outlet Name is required',
                validate: value => value.trim() !== '' || 'Outlet Name cannot be empty',
                pattern: {
                  value: /^[a-zA-Z0-9]+( [a-zA-Z0-9]+)*$/,
                  message: 'Outlet Name should contain only alphabets, numbers, and spaces'
                }
              })}
              onInput={e => {
                e.currentTarget.value = e.currentTarget.value.replace(/[^a-zA-Z0-9\s]/g, '');
              }}
              autoFocus
            />
            {errors?.name && <DisplayFieldErrors displayText={errors.name.message!} />}
          </div>
          <div className="form-group">
            <Form.Label htmlFor="phoneNumber">Phone Number</Form.Label>
            <Form.Control
              type="tel"
              id="phoneNumber"
              placeholder="09065068997"
              {...register('phoneNumber', {
                required: 'Phone number is required',
                pattern: {
                  value: /^[0-9]+$/,
                  message: 'Phone number must contain only digits'
                },
                validate: isValidPhoneNumber
              })}
              autoFocus
            />
            {errors?.phoneNumber && <DisplayFieldErrors displayText={errors.phoneNumber.message!} />}
          </div>
          <div className="form-group">
            <Form.Label htmlFor="address">Address</Form.Label>
            <Form.Control
              type="text"
              id="address"
              placeholder="No 20, lekki phase 1"
              {...register('address', {
                required: 'Address is required',
                minLength: {
                  value: 5,
                  message: 'Address must be at least 5 characters long.'
                },
                validate: value => value.trim() !== '' || 'Address cannot be empty'
              })}
            />
            {errors?.address && <DisplayFieldErrors displayText={errors.address.message!} />}
          </div>

          <SubmitButton
            buttonText="Create Outlet"
            loading={loading}
            onClick={onCreateOutlet}
            disabled={!isValid || loading}
          ></SubmitButton>
        </Form>
      </ModalComponent.Body>
    </ModalComponent>
  );
};

export default CreateOutletModal;
